import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import cn from 'classnames'
import { Select } from 'components/Select'
import { Input } from 'components/Input'
import { LABELS, PLACEHOLDERS } from 'constants/txt'
import {
  addHospital,
  editHospital
} from 'features/HealthSystemDetails/HospitalsTable/api'
import { getUser } from 'redux/store/user/getters'
import { fetchDepartments } from 'pages/CRUDUser/api'
import { setLoading } from 'redux/store/common/slice'
import { formatOptionNameValue } from 'helper/optionsFormatters'
import {
  formatPhoneNumber,
  validateMessages,
  websiteValidationRegex
} from 'helper/common'
import { TAddEditHospitalData } from 'features/HealthSystemDetails/types'
import {
  TAddEditHospitalForm,
  TAddEditHospitalProps,
  TAddEditHospitalErrors,
  TAddEditHospitalFormFields
} from './forms.addEditHospital.d'
import { TOption } from 'components/Select/types'
import { hospitalInitialValues } from './constants'
import { fetchStateOptions } from '../../features/PaymentDetails/api'
import { RowLayout } from '../../features/MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import './forms.addEditHospital.scss'

export const AddEditHospital = ({
  className,
  form,
  callback,
  hsId,
  data,
  onChange,
  setIsActive,
  setIsDepartmentsChanged
}: TAddEditHospitalProps) => {
  const [departmentsList, setDepartmentsList] = useState<TOption[]>([])
  const [stateList, setStateList] = useState<TOption[]>([])
  const [errors, setErrors] = useState<TAddEditHospitalErrors>({})
  const [fields, setFields] = useState<TAddEditHospitalFormFields>([])
  const [initialDepartments, setInitialDepartments] = useState<string[]>([])
  const user = useSelector(getUser)
  const dispatch = useDispatch()

  const phoneNumber = Form.useWatch('phone_number', form)

  useEffect(() => {
    if (user.community && hsId) {
      fetchDepartments(user.community, hsId)
        .then((resp) => {
          if (!resp.data?.results) {
            return
          }
          setDepartmentsList(resp.data.results.map(formatOptionNameValue))
        })
        .finally(() => dispatch(setLoading(false)))
    }
  }, [user.community, hsId, dispatch])
  useEffect(() => {
    if (data?.uuid) {
      const formattedData = {}
      for (const key in data) {
        if (typeof data[key] === 'object' && key === 'address') {
          for (const item in data[key]) {
            formattedData[item] = data[key][item]
          }
        } else if (key === 'departments') {
          formattedData[key] = data[key].map((i) => ({
            label: i.name,
            value: i.uuid
          }))
          // Store initial departments UUIDs for comparison
          setInitialDepartments(data[key].map((i) => i.uuid))
        } else {
          formattedData[key] = data[key]
        }
      }
      const fields = Object.keys(formattedData).map((i) => ({
        name: i,
        value: formattedData[i]
      }))
      setFields(fields)
    } else {
      // Reset initial departments when creating new hospital
      setInitialDepartments([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.uuid])

  const getStateList = async () => {
    await fetchStateOptions().then((res) => {
      setStateList(
        res.data.results.map((option) => ({
          label: option.id,
          value: option.id
        }))
      )
    })
  }

  useEffect(() => {
    getStateList()
  }, [])

  const handleAddEditHospital = (formData: TAddEditHospitalForm) => {
    dispatch(setLoading(true))
    setIsActive(false)
    const number = !formData?.phone_number?.toString()?.includes('+1')
      ? '+1' + formData.phone_number
      : formData.phone_number
    const hospitalData: TAddEditHospitalData = {
      name: formData.name,
      legal_name: formData.legal_name,
      website: formData.website ? formData.website : '',
      phone_number: formData.phone_number ? number : '',
      departments: formData.departments
        ? typeof formData.departments[0] === 'object'
          ? formData.departments.map((i) => i.value)
          : formData.departments
        : undefined,
      address: {
        state: formData.state,
        street: formData.street,
        zip_code: formData.zip_code,
        city: formData.city
      }
    }
    if (!user.community || !hsId) {
      dispatch(setLoading(false))
      return
    }
    if (data?.uuid) {
      editHospital(user.community, hsId, hospitalData, data.uuid)
        .then(() => {
          if (callback) {
            callback()
          }
        })
        .catch((e) => {
          setErrors(e.data)
        })
        .finally(() => {
          dispatch(setLoading(false))
        })
    } else {
      addHospital(user.community, hsId, hospitalData)
        .then(() => {
          if (callback) {
            callback()
          }
        })
        .catch((e) => {
          setErrors(e.data)
        })
        .finally(() => {
          dispatch(setLoading(false))
        })
    }
  }

  return (
    <CustomForm<TAddEditHospitalForm>
      form={form}
      onFinish={handleAddEditHospital}
      fields={fields}
      initialValues={hospitalInitialValues}
      validateMessages={validateMessages}
      onFieldsChange={() => setErrors({})}
      onValuesChange={(data, changedValues) => {
        if (data?.departments !== undefined) {
          const currentDepartments = Array.isArray(data?.departments)
            ? data.departments.map((dept: TOption | string) =>
                typeof dept === 'object' ? dept.value : dept
              )
            : []
          const hasRemovedDepartment = initialDepartments.some(
            (initialDeptUuid) => !currentDepartments.includes(initialDeptUuid)
          )
          if (hasRemovedDepartment) {
            setIsDepartmentsChanged(true)
          }
        }
        onChange(changedValues)
      }}
      id="add-edit-hospital-form"
      className={cn('add-hospital-form', className)}
    >
      <RowLayout title="Business information">
        <>
          <Input
            propsItem={{
              name: 'name',
              label: LABELS.HOSPITAL_NAME_DOING_BUSINESS_AS,
              help: errors.name && errors.name[0],
              validateStatus: errors.name && 'error',
              rules: [
                {
                  required: true
                }
              ]
            }}
            propsInput={{ maxLength: 64, showCount: true }}
          />
          <Input
            propsItem={{
              name: 'legal_name',
              label: LABELS.HOSPITAL_LEGAL_NAME,
              help: errors.legal_name && errors.legal_name[0],
              validateStatus: errors.legal_name && 'error'
            }}
            propsInput={{ maxLength: 64, showCount: true }}
          />
          <div className="row two-columns">
            <Input
              propsInput={{ placeholder: PLACEHOLDERS.WEBSITE }}
              propsItem={{
                name: 'website',
                label: LABELS.WEBSITE,
                help: errors.website && errors.website[0],
                validateStatus: errors.website && 'error',
                rules: [
                  {
                    pattern: websiteValidationRegex,
                    validateTrigger: ['onSubmit']
                  }
                ]
              }}
            />
            <Input.Phone
              propsInputNumber={{
                controls: false,
                formatter: formatPhoneNumber,
                prefix: phoneNumber ? '+1' : ''
                // parser: parserPhoneNumber
              }}
              propsItem={{
                name: 'phone_number',
                label: LABELS.PHONE_NUMBER,
                rules: [
                  {
                    // required: true,
                    pattern: /^[+, 0-9]{10,12}$/,
                    validateTrigger:
                      phoneNumber?.length === 10 ? ['onSubmit'] : ['onBlur']
                  }
                ]
              }}
            />
          </div>
        </>
      </RowLayout>
      <div className="add-hospital-form__divider mb-24" />
      <RowLayout title="Address">
        <>
          <Input
            propsItem={{
              name: 'street',
              label: LABELS.STREET,
              rules: [
                {
                  required: true
                }
              ]
            }}
          />
          <div className="row gap-24">
            <div className="city-input">
              <Input
                propsItem={{
                  name: 'city',
                  label: LABELS.CITY,
                  rules: [
                    {
                      required: true
                    }
                  ]
                }}
              />
            </div>
            <div className="short-field">
              <Select
                options={stateList}
                className="long-select"
                propsItem={{
                  label: LABELS.STATE,
                  name: 'state',
                  help: errors.address?.state && errors.address?.state[0],
                  validateStatus: errors.address?.state && 'error',
                  rules: [
                    {
                      required: true
                    }
                  ]
                }}
                propsSelect={{
                  required: true,
                  placeholder: PLACEHOLDERS.PLEASE_SELECT
                }}
              />
            </div>
            <div className="short-field">
              <Input
                className="column-half"
                propsItem={{
                  name: 'zip_code',
                  label: LABELS.ZIP_CODE,
                  help: errors.address?.zip_code && errors.address?.zip_code[0],
                  validateStatus: errors.address?.zip_code && 'error',
                  rules: [
                    {
                      required: true
                    }
                  ]
                }}
              />
            </div>
          </div>
        </>
      </RowLayout>
      <div className="add-hospital-form__divider mb-24" />
      <RowLayout title="Departments">
        <div className="row">
          <Select.Multi
            options={departmentsList}
            propsItem={{
              label: LABELS.DEPARTMENTS,
              name: 'departments'
            }}
            propsSelect={{
              placeholder: PLACEHOLDERS.PLEASE_SELECT
            }}
          />
        </div>
      </RowLayout>
    </CustomForm>
  )
}
